import { z } from 'zod';

import { hostnameSchema, isoDateTimeSchema, uuidSchema } from '../common/primitives';

/**
 * Kundendomains — der Teil des Vertrags, der eine Website von einem Container
 * unterscheidet.
 *
 * ## Warum eine Domain überhaupt einen Zustand hat
 *
 * Eine eingetragene Domain ist noch keine benutzbare Domain. Zwischen beidem
 * liegt die DNS-Vorprüfung, und die ist nicht bloß Höflichkeit gegenüber Let's
 * Encrypt: **Sie ist die Zugangskontrolle.** Ohne sie trägt jemand
 * `konkurrenz.de` ein, wir nehmen den Verkehr entgegen, sobald deren A-Record
 * einmal auf uns zeigt, und wir holen ein Zertifikat für eine Domain, die uns
 * nicht gehört.
 *
 * Deshalb ist `verificationStatus` ein **Ist**-Zustand: Er beschreibt, was das
 * DNS zuletzt gesagt hat, nicht was der Betreiber sich wünscht. Der Wunsch ist
 * das Vorhandensein der Zeile.
 */

// -- Zustände ----------------------------------------------------------------

export const DOMAIN_ROLES = {
  /** Der kanonische Name. Hierhin wird umgeleitet, hierauf lautet das Zertifikat. */
  apex: 'apex',
  /** Der `www`-Begleiter. Wird per 301 auf den Apex umgeleitet. */
  www: 'www',
} as const;

export type DomainRole = (typeof DOMAIN_ROLES)[keyof typeof DOMAIN_ROLES];

export const domainRoleSchema = z.enum([DOMAIN_ROLES.apex, DOMAIN_ROLES.www]);

export const DOMAIN_VERIFICATION_STATUS = {
  /**
   * Noch nicht bestätigt — und ausdrücklich **nicht** dasselbe wie
   * fehlgeschlagen. Ein Nameserver-Aussetzer landet hier, ein falscher A-Record
   * dagegen auf `failed`. Die Unterscheidung entscheidet darüber, ob jemand
   * etwas tun muss.
   */
  pending: 'pending',
  /** Das DNS zeigt nachweislich auf unsere Edge. */
  verified: 'verified',
  /** Das DNS zeigt nachweislich woandershin oder gar nicht. */
  failed: 'failed',
} as const;

export type DomainVerificationStatus =
  (typeof DOMAIN_VERIFICATION_STATUS)[keyof typeof DOMAIN_VERIFICATION_STATUS];

export const domainVerificationStatusSchema = z.enum([
  DOMAIN_VERIFICATION_STATUS.pending,
  DOMAIN_VERIFICATION_STATUS.verified,
  DOMAIN_VERIFICATION_STATUS.failed,
]);

// -- Der FQDN einer Kundendomain ---------------------------------------------

/**
 * Kleinste Anzahl Labels einer Kundendomain.
 *
 * `de` ist keine Domain, die jemand besitzen kann, und `localhost` ebenfalls
 * nicht. Zwei Labels sind die untere Schranke, die ohne eine vollständige
 * Public-Suffix-Liste auskommt.
 */
export const MIN_DOMAIN_LABELS = 2;

/**
 * Der vollqualifizierte Name einer Kundendomain.
 *
 * Enger als `hostnameSchema`: mindestens zwei Labels, kein abschließender Punkt.
 * Internationale Domains müssen bereits als Punycode (`xn--…`) übergeben werden
 * — eine Umwandlung an dieser Stelle wäre eine zweite Normalisierung neben der
 * des Registrars, und zwei Normalisierungen ergeben früher oder später zwei
 * verschiedene Namen für dieselbe Domain.
 */
export const fqdnSchema = hostnameSchema.superRefine((value, ctx) => {
  if (value.split('.').length < MIN_DOMAIN_LABELS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Muss eine vollständige Domain mit mindestens zwei Namensteilen sein, z. B. "kunde.de".',
    });
  }
});

// -- Sicht für die Administrations-API ---------------------------------------

export const domainViewSchema = z.object({
  id: uuidSchema,
  siteId: uuidSchema,
  fqdn: hostnameSchema,
  role: domainRoleSchema,
  /** Bei `www` der Apex, auf den umgeleitet wird; sonst `null`. */
  redirectToFqdn: hostnameSchema.nullable(),
  verificationStatus: domainVerificationStatusSchema,
  verifiedAt: isoDateTimeSchema.nullable(),
  lastCheckedAt: isoDateTimeSchema.nullable(),
  /** Der letzte Befund im Klartext, mit Handlungsanweisung. */
  lastCheckError: z.string().nullable(),
  /** Was zuletzt aufgelöst wurde. Der Fehlerbericht, nicht bloß ein Protokoll. */
  resolvedAddresses: z.array(z.string()).nullable(),
  /**
   * Kennt der Node diese Domain bereits?
   *
   * `false` heißt: Die Domain ist verifiziert, aber der Worker fährt noch eine
   * Spezifikation ohne sie. Solange das gilt, taucht sie in der Edge-Config
   * **nicht** auf — sonst routete die Edge auf einen Worker, der den
   * Host-Header nicht kennt, und der Kunde sähe ein 404.
   */
  liveOnNode: z.boolean(),
  createdAt: isoDateTimeSchema,
});

export type DomainView = z.infer<typeof domainViewSchema>;
